export const NATIVE_RAGFLOW_CHAT_BACKEND = 'native_ragflow';
export const AGENT_MESH_CHAT_BACKEND = 'agent_mesh';
export const FASB_CHAT_BACKEND = 'opensearch_fasb';
export const SOUTHWEST_AGENTCORE_DEMO_CHAT_BACKEND = 'southwest_agentcore_demo';
export const AGENTCORE_CHAT_BACKEND = 'agentcore';

type WorkspaceLike = {
  name?: string | null;
  template_name?: string | null;
  workspace_config?: Record<string, unknown> | null;
};

function normalizeOptionalBoolean(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'y', 'on'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'n', 'off'].includes(normalized)) return false;
  }
  return null;
}

function getWorkspaceConfig(workspace: WorkspaceLike | null | undefined): Record<string, unknown> {
  return workspace?.workspace_config && typeof workspace.workspace_config === 'object'
    ? (workspace.workspace_config as Record<string, unknown>)
    : {};
}

function getChatConfig(workspace: WorkspaceLike | null | undefined): Record<string, unknown> {
  const config = getWorkspaceConfig(workspace);
  return config.chat && typeof config.chat === 'object'
    ? (config.chat as Record<string, unknown>)
    : {};
}

function normalizeChatBackend(value: unknown): string {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (['ragflow', 'native', 'native_rag', 'native_ragflow'].includes(normalized)) {
    return NATIVE_RAGFLOW_CHAT_BACKEND;
  }
  if (['agent_mesh', 'agent_mesh_retrieval'].includes(normalized)) {
    return AGENT_MESH_CHAT_BACKEND;
  }
  if (normalized === 'opensearch_fasb') {
    return FASB_CHAT_BACKEND;
  }
  if (normalized === SOUTHWEST_AGENTCORE_DEMO_CHAT_BACKEND) {
    return SOUTHWEST_AGENTCORE_DEMO_CHAT_BACKEND;
  }
  if (['agentcore', 'agentcore_agent'].includes(normalized)) {
    return AGENTCORE_CHAT_BACKEND;
  }
  return normalized;
}

export function getWorkspaceChatBackend(workspace: WorkspaceLike | null | undefined): string {
  const config = getWorkspaceConfig(workspace);
  const chat = getChatConfig(workspace);
  const domainName = String(workspace?.name || '').trim().toLowerCase();
  const templateName = String(workspace?.template_name || '').trim().toLowerCase();
  const opensearchIndex = String(config.opensearch_index || '').trim().toLowerCase();

  const configuredBackend = normalizeChatBackend(chat.backend_type || config.backend_type);
  if (configuredBackend) return configuredBackend;

  if (domainName === 'fasb' || opensearchIndex.startsWith('fasb')) {
    return FASB_CHAT_BACKEND;
  }

  if (templateName === 'agent_mesh_retrieval' || ['agent_mesh', 'agentmesh'].includes(domainName)) {
    return AGENT_MESH_CHAT_BACKEND;
  }

  if (templateName === 'agentcore_agent') {
    return AGENTCORE_CHAT_BACKEND;
  }

  return NATIVE_RAGFLOW_CHAT_BACKEND;
}

export function workspaceChatRequiresKnowledgeBase(
  workspace: WorkspaceLike | null | undefined
): boolean {
  const config = getWorkspaceConfig(workspace);
  const chat = getChatConfig(workspace);

  const explicitRequirement = normalizeOptionalBoolean(
    chat.requires_knowledge_base ?? config.chat_requires_knowledge_base
  );
  if (explicitRequirement !== null) return explicitRequirement;

  return getWorkspaceChatBackend(workspace) === NATIVE_RAGFLOW_CHAT_BACKEND;
}

export function workspaceSupportsChatWithoutKnowledgeBase(
  workspace: WorkspaceLike | null | undefined
): boolean {
  return !workspaceChatRequiresKnowledgeBase(workspace);
}
